import React, { useCallback } from 'react';
import { ViewOption } from './ViewSwitcher';
import { ViewType } from 'app/src/components/ViewsGenerics';
import cx from 'classnames';
import { forTemplate } from '@trello/i18n';
// eslint-disable-next-line @trello/less-matches-component
import styles from './ViewSwitcher.less';
const format = forTemplate('view_switcher');
import { OverflowMenuHorizontalIcon } from '@trello/nachos/icons/overflow-menu-horizontal';
import { Button } from '@trello/nachos/button';
import { Popover, usePopover } from '@trello/nachos/popover';
import { BoardHeaderTestIds } from '@trello/test-ids';

interface ViewSwitcherButtonProps {
  idBoard: string;
  viewOptions: { [key in ViewType]: ViewOption };
  backgroundBrightness: string;
  currentView: ViewType | null;
  enabledViews: ViewType[];
  setEnabledViews: (views: ViewType[]) => void;
  idOrg?: string;
}

export const ViewSwitcherButtons: React.FunctionComponent<ViewSwitcherButtonProps> = ({
  idBoard,
  backgroundBrightness,
  currentView,
  enabledViews,
  viewOptions,
  // setEnabledViews,
}) => {
  const onViewClick = useCallback(
    (currentlyOnView: boolean, view: ViewOption) => {
      return () => {
        if (!currentlyOnView) {
          view.navigateTo(idBoard, view.name);
        }
      };
    },
    [idBoard],
  );

  const onKeyUp = useCallback(
    (e: React.KeyboardEvent<Element>, callback: () => void) => {
      if (e.key === 'Enter') {
        callback();
      }
    },
    [],
  );

  const { triggerRef, toggle, popoverProps } = usePopover<HTMLButtonElement>();

  return (
    <>
      {enabledViews.map((viewName: ViewType) => {
        const view = viewOptions[viewName];
        const currentlyOnView = viewName === currentView;
        const iconBrightness =
          backgroundBrightness === 'dark' ? 'dark' : 'light';
        const inverseBrightness =
          backgroundBrightness === 'dark' ? 'light' : 'dark';
        return (
          <a
            className={cx(
              styles.viewSwitcherButton,
              currentlyOnView && styles.viewSwitcherButtonActive,
            )}
            key={viewName}
            tabIndex={0}
            role={'link'}
            // eslint-disable-next-line react/jsx-no-bind
            onKeyUp={(e) => onKeyUp(e, onViewClick(currentlyOnView, view))}
            onClick={onViewClick(currentlyOnView, view)}
            aria-label={format(viewName)}
            data-testid={`view-switcher-button-${viewName}`}
          >
            <span
              className={styles.viewSwitcherIcon}
              data-testid={BoardHeaderTestIds.ViewSwitcherButtonIcon}
            >
              {React.createElement(view.icon, {
                size: 'small',
                color: currentlyOnView ? iconBrightness : inverseBrightness,
              })}
            </span>
            <span data-testid={BoardHeaderTestIds.ViewSwitcherButtonText}>
              {format(viewName)}
            </span>
          </a>
        );
      })}
      <Button
        className={styles.overflowIcon}
        iconBefore={
          <OverflowMenuHorizontalIcon
            color={backgroundBrightness === 'dark' ? 'light' : 'gray'}
          />
        }
        ref={triggerRef}
        onClick={toggle}
        data-test-id={BoardHeaderTestIds.ViewSwitcherButtonMore}
        appearance={
          backgroundBrightness === 'dark' ? 'transparent' : 'transparent-dark'
        }
      />
      <Popover {...popoverProps} title={'Add and edit views'}>
        views go here
      </Popover>
    </>
  );
};
